import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { GraduationCap, Books, ArrowRight, UserPlus, SignIn } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { SBL_PROGRAMMES } from "../lib/programmes";

const SBL_LOGO = "/sbl-logo.jpeg";

export default function FreeCoursesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [regForm, setRegForm] = useState({
    name: "", email: "", phone: "", password: "",
    school_name: "", position: "",
    interest_type: "free_course", interest_choice: ""
  });
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    api.get("/courses/free").then(r => { setCourses(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleStartCourse = (course) => {
    if (user) {
      navigate(`/courses/${course.id}`);
    } else {
      setSelectedCourse(course);
      setShowRegister(true);
    }
  };

  const handleRegister = async () => {
    if (!regForm.name || !regForm.email || !regForm.password) {
      toast.error("Please fill in Name, Email and Password");
      return;
    }
    if (!regForm.phone || !regForm.school_name || !regForm.position) {
      toast.error("Please provide your Phone, Employer/School and Position");
      return;
    }
    if (regForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setRegistering(true);
    try {
      await api.post("/auth/free-register", regForm);
      toast.success("Registration successful! Signing you in...");
      // Auto-login
      const loginRes = await api.post("/auth/login", { email: regForm.email, password: regForm.password });
      if (loginRes.data) {
        window.location.href = selectedCourse ? `/courses/${selectedCourse.id}` : "/dashboard";
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    }
    setRegistering(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="free-courses-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={SBL_LOGO} alt="School Builder Lab Africa" className="w-9 h-9 object-contain rounded-sm" />
            <span className="text-base sm:text-lg font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              School Builder Lab Africa
            </span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard">
                <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none text-sm">Go to Dashboard</Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="rounded-none text-sm gap-2"><SignIn size={16} /> Sign In</Button>
                </Link>
                <Button onClick={() => { setSelectedCourse(null); setShowRegister(true); }} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none text-sm gap-2">
                  <UserPlus size={16} /> Register Free
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-800 to-slate-900 text-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <Badge className="mb-4 bg-white/20 text-white rounded-none text-xs border-0">100% Free</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Free Leadership Courses
          </h1>
          <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto">
            Start your leadership journey today with our free courses. No fees, no obligations. Complete a course and earn your completion badge.
          </p>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3].map(i => <div key={i} className="h-48 bg-slate-100 animate-pulse" />)}
            </div>
          ) : courses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course, idx) => (
                <Card key={course.id} className="border border-slate-200 rounded-none shadow-sm hover:shadow-md transition-all" data-testid={`free-course-${idx}`}>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-blue-800/10 flex items-center justify-center mb-4">
                      <Books size={24} className="text-blue-800" />
                    </div>
                    <Badge className="mb-2 rounded-none text-xs bg-emerald-100 text-emerald-700 border-0">Free</Badge>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{course.title}</h3>
                    <p className="text-sm text-slate-600 mb-4 line-clamp-3">{course.description}</p>
                    {course.programme_title && (
                      <p className="text-xs text-slate-500 mb-4">Programme: {course.programme_title}</p>
                    )}
                    <Button onClick={() => handleStartCourse(course)} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid={`start-free-course-${idx}`}>
                      Start Course <ArrowRight size={16} />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <GraduationCap size={48} className="text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Free Courses Coming Soon</h3>
              <p className="text-sm text-slate-500 mb-6">We're preparing exciting free content for you. Check back soon!</p>
              <Link to="/">
                <Button variant="outline" className="rounded-none">Back to Home</Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* CTA to full programmes */}
      <section className="bg-slate-50 border-t border-slate-200 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Ready for More?
          </h2>
          <p className="text-base text-slate-600 mb-6 max-w-2xl mx-auto">
            Explore our full range of programmes to take your career to the next level.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/programmes-catalogue">
              <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2">
                View All Programmes <ArrowRight size={16} />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="rounded-none">Back to Home</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Registration Dialog */}
      <Dialog open={showRegister} onOpenChange={setShowRegister}>
        <DialogContent className="sm:max-w-md rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
              {selectedCourse ? `Register to Start: ${selectedCourse.title}` : "Create Your Free Account"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 mb-3">Sign up to access free courses, track your progress, and earn completion badges. All fields marked <span className="text-red-500">*</span> are required.</p>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Full Name <span className="text-red-500">*</span></Label>
              <Input value={regForm.name} onChange={e => setRegForm({ ...regForm, name: e.target.value })} className="mt-1 rounded-none" placeholder="Your full name" data-testid="free-reg-name" />
            </div>
            <div>
              <Label className="text-sm">Email <span className="text-red-500">*</span></Label>
              <Input type="email" value={regForm.email} onChange={e => setRegForm({ ...regForm, email: e.target.value })} className="mt-1 rounded-none" placeholder="you@example.com" data-testid="free-reg-email" />
            </div>
            <div>
              <Label className="text-sm">Phone <span className="text-red-500">*</span></Label>
              <Input value={regForm.phone} onChange={e => setRegForm({ ...regForm, phone: e.target.value })} className="mt-1 rounded-none" placeholder="+234..." data-testid="free-reg-phone" />
            </div>
            <div>
              <Label className="text-sm">Current Employer / School Name <span className="text-red-500">*</span></Label>
              <Input value={regForm.school_name} onChange={e => setRegForm({ ...regForm, school_name: e.target.value })} className="mt-1 rounded-none" placeholder="e.g. Greenfield Academy" data-testid="free-reg-employer" />
            </div>
            <div>
              <Label className="text-sm">Your Role / Position <span className="text-red-500">*</span></Label>
              <Input value={regForm.position} onChange={e => setRegForm({ ...regForm, position: e.target.value })} className="mt-1 rounded-none" placeholder="e.g. Head of School, Teacher" data-testid="free-reg-position" />
            </div>
            <div>
              <Label className="text-sm">Programme you're curious about (optional)</Label>
              <Select value={regForm.interest_choice || "none"} onValueChange={v => setRegForm({ ...regForm, interest_choice: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="free-reg-programme-interest">
                  <SelectValue placeholder="Select a programme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not sure yet</SelectItem>
                  {SBL_PROGRAMMES.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Password <span className="text-red-500">*</span></Label>
              <Input type="password" value={regForm.password} onChange={e => setRegForm({ ...regForm, password: e.target.value })} className="mt-1 rounded-none" placeholder="Min 6 characters" data-testid="free-reg-password" />
            </div>
            <Button onClick={handleRegister} disabled={registering} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="free-reg-submit">
              {registering ? "Creating Account..." : "Register & Start Learning"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Already have an account? <Link to="/login" className="text-blue-800 font-medium underline">Sign In</Link>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
